import {Controller} from "../controller/Controller";
import {
    Avatar, classNames,
    createTheme, layout, MaterialIcon, PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow, typography,
    useAsync,
    View,
    ViewContent
} from "@variocube/cube-ui";
import React, {useCallback, useEffect} from "react";
import {themeOptions} from "../theme";
import {useLocalization} from "../../i18n";
import {Box, Lock, LockStatus} from "@variocube/app-api";
import {BoxProperties} from "./BoxProperties";
import {LockState} from "./LockState";

interface MaintenanceOnlyViewProps {
    controller: Controller;
    onClose: () => void;
}

export function MaintenanceOnlyView({controller, onClose}: MaintenanceOnlyViewProps) {
    const {t} = useLocalization();

    const {result, loading, error, execute} = useAsync(async () => Promise.all([
        (await controller.getBoxes()).filter(b => !!b.maintenanceRequiredAt),
        controller.getLocks()
    ]), [controller]);

    const handleLockStatus = useCallback(() => {
        console.log('lock status changed');
        execute();
    }, [execute]);

    useEffect(() => {
        controller.addLockStatusListener(handleLockStatus);
        return () => controller.removeLockStatusListener(handleLockStatus);
    }, [controller, handleLockStatus]);

    const handleOpen = useCallback(async (lock: string)=> {
        await controller.open(lock);
        execute();
    }, [controller]);

    const handleComplete = useCallback(async (boxNumber: string)=> {
        await controller.updateBoxMaintenanceState(boxNumber, false);
        execute();
    }, [controller]);

    return (
        <View className={settingsTheme}>
            <ViewContent className={classNames(layout.vertical)}>
                <div className={classNames(layout.vertical, layout.marginYSmall, layout.paddingMedium)}>
                    <span className={typography.title}>{t('MaintenanceRequestsTitle')}</span>
                    <span>{t('MaintenanceRequestsText')}</span>

                    <div className={classNames(layout.vertical, layout.marginYMedium)}>
                        {error && (
                            <span className={typography.colorError}>{t('MaintenanceRequestsLoadError')}</span>
                        )}

                        {loading && (
                            <span className={typography.bigger}>{t('MaintenanceLoading')}</span>
                        )}
                    </div>
                </div>
                {result && (
                    <div style={{ flexGrow: 1 }}>
                        <MaintenanceRequiredBoxList
                            boxes={result[0]}
                            locks={result[1]}
                            onOpen={handleOpen}
                            onComplete={handleComplete}
                        />
                    </div>
                )}
                <div className={classNames(layout.vertical, layout.paddingMedium, layout.alignCenter, layout.center)}>
                    <PushButton onClick={onClose}>
                        {t("Close")}
                    </PushButton>
                </div>
            </ViewContent>
        </View>
)
}

interface MaintenanceRequiredBoxListProps {
    boxes: Box[];
    locks: Lock[];
    onOpen: (boxNumber: string) => void;
    onComplete: (boxNumber: string) => void;
}

function MaintenanceRequiredBoxList({boxes, locks, onOpen, onComplete}: MaintenanceRequiredBoxListProps) {
    const {t} = useLocalization();

    function getLock(handle: string) {
        return locks.find(l => l.handle === handle);
    }

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>{t("Box")}</TableCell>
                    <TableCell>{t("Types")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Enabled")}</TableCell>
                    <TableCell>{t("Maintenance")}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {boxes.length == 0 && (
                    <TableRow>
                        <TableCell colSpan={5}>{t('MaintenanceEmpty')}</TableCell>
                    </TableRow>
                )}
                {boxes.map((box, index) => (
                    <TableRow key={box.number}>
                        <TableCell><Avatar>{box.number}</Avatar></TableCell>
                        <TableCell><BoxProperties box={box} /></TableCell>
                        <TableCell><LockState lock={getLock(box.lock)} /></TableCell>
                        <TableCell>
                            <div
                                className={classNames(typography.bigger, typography.alignCenter)}
                            >
                                {box.disabled ? MaterialIcon.do_not_disturb_on : MaterialIcon.check}
                            </div>
                        </TableCell>
                        <TableCell>
                            <PushButton
                                icon={MaterialIcon.lock_open}
                                primary
                                onClick={() => onOpen(box.lock)}
                            >
                                {t("Open")}
                            </PushButton>
                            <span>{" "}</span>
                            <PushButton
                                icon={MaterialIcon.check}
                                success
                                onClick={() => onComplete(box.number)}
                            >
                                {t("MaintenanceLeave")}
                            </PushButton>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

const settingsTheme = createTheme({
    ...themeOptions,
    colorBackground: "#fff",
    colorForeground: "#333",
});